import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES, ASSIGNABLE_ROLES, frDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPlus, Trash2, CalendarOff, Plus, Shield, Send, Clock, Users } from "lucide-react";
import { KpiStrip } from "@/components/Widget";
import { toast } from "sonner";

const ROLE_CLS = {
  admin: "bg-[#FF6A00]/10 text-[#FF6A00] border-[#FF6A00]/25",
  commercial: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  logistique: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  technicien: "bg-purple-500/10 text-purple-400 border-purple-500/25",
};

export default function Team() {
  const { user, org } = useAuth();
  const [members, setMembers] = useState([]);
  const [unav, setUnav] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [uOpen, setUOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [invite, setInvite] = useState({ name: "", email: "", role: "commercial" });
  const [delTarget, setDelTarget] = useState(null);
  const [uForm, setUForm] = useState({ user_id: "", start_date: today, end_date: today, reason: "" });

  const load = () => {
    api.get("/collaborators").then(({ data }) => setMembers(data));
    api.get("/unavailabilities").then(({ data }) => setUnav(data));
  };
  useEffect(() => { load(); }, []);

  const usersLimit = org?.access?.users_limit;
  const atUserLimit = usersLimit != null && members.length >= usersLimit;
  const doInvite = async () => {
    if (atUserLimit) return toast.error(`Votre abonnement autorise ${usersLimit} utilisateur(s) maximum.`);
    if (!invite.name || !invite.email) return toast.error("Le nom et l'email sont requis");
    try {
      const { data } = await api.post("/collaborators", invite);
      toast.success(data?.email_sent
        ? "Invitation envoyée — le collaborateur va recevoir un email pour créer son mot de passe"
        : "Invitation créée. Si l'email n'arrive pas, utilisez « Renvoyer l'invitation ».");
      setInvite({ name: "", email: "", role: "commercial" });
      setInviteOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const resendInvite = async (id) => {
    try {
      const { data } = await api.post(`/collaborators/${id}/resend`);
      toast.success(data?.email_sent ? "Invitation renvoyée" : "Nouveau lien généré. Si l'email n'arrive pas, réessayez dans quelques minutes.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/collaborators/${id}/role`, { role }); toast.success("Rôle mis à jour"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delMember = async (id) => { try { await api.delete(`/collaborators/${id}`); toast.success("Membre et données supprimés"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); } finally { setDelTarget(null); } };

  const addUnav = async () => {
    try {
      await api.post("/unavailabilities", { ...uForm, user_id: uForm.user_id || null });
      toast.success("Indisponibilité ajoutée");
      setUForm({ user_id: "", start_date: today, end_date: today, reason: "" });
      setUOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delUnav = async (id) => { await api.delete(`/unavailabilities/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Équipe</h1>
        <p className="mt-1 text-sm text-zinc-500">Collaborateurs, rôles et indisponibilités.</p>
      </div>
      <KpiStrip testid="team-kpis" items={[
        { icon: Users, label: "Membres", value: `${members.length}${usersLimit != null ? ` / ${usersLimit}` : ""}`, tint: "#FF6A00" },
        { icon: Shield, label: "Administrateurs", value: members.filter((m) => (m.roles || []).includes("admin")).length, tint: "#FFD500" },
        { icon: Send, label: "Invitations en attente", value: members.filter((m) => m.status === "invited").length, tint: "#60a5fa" },
      ]} />
      <Tabs defaultValue="members">
        <TabsList className="rounded-full bg-zinc-900">
          <TabsTrigger value="members" className="rounded-full data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white">Collaborateurs</TabsTrigger>
          <TabsTrigger value="unav" data-testid="tab-unav" className="rounded-full data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white">Indisponibilités</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-zinc-500">{members.length}{usersLimit != null ? ` / ${usersLimit}` : ""} membre(s) dans votre organisation.</p>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild><Button data-testid="invite-btn" className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><UserPlus className="mr-1 h-4 w-4" />Inviter</Button></DialogTrigger>
              <DialogContent className="border-zinc-800 bg-zinc-950">
                <DialogHeader><DialogTitle className="text-white">Inviter un collaborateur</DialogTitle><DialogDescription className="text-zinc-400">Le collaborateur recevra un email pour créer son propre mot de passe.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-zinc-400">Nom</Label><Input data-testid="invite-name" className="mt-1 border-zinc-800 bg-zinc-900" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></div>
                  <div><Label className="text-zinc-400">Email</Label><Input data-testid="invite-email" className="mt-1 border-zinc-800 bg-zinc-900" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
                  <div>
                    <Label className="text-zinc-400">Rôle</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger data-testid="invite-role" className="mt-1 border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">{ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLES[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <p className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-400">
                    Un email d'invitation sera envoyé. Le collaborateur choisira lui-même son mot de passe en toute confidentialité.
                  </p>
                  <Button data-testid="save-invite" onClick={doInvite} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Envoyer l'invitation</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
            {members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex flex-wrap items-center gap-4 border-b border-zinc-800 p-4 last:border-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#FF6A00]/30 to-[#FFD500]/20 font-semibold text-white">{m.name.slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-white">{m.name} {m.is_self && <span className="text-xs text-zinc-500">(vous)</span>}</p>
                  <p className="truncate text-sm text-zinc-500">{m.email}</p>
                </div>
                {m.status === "invited" && (
                  <span data-testid={`pending-${m.id}`} className="inline-flex items-center gap-1 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-400">
                    <Clock className="h-3 w-3" />Invitation en attente
                  </span>
                )}
                {m.is_self ? (
                  <span className={`rounded-full border px-3 py-1 text-xs font-medium ${ROLE_CLS[m.roles[0]] || ""}`}>{ROLES[m.roles[0]] || m.roles[0]}</span>
                ) : (
                  <Select value={m.roles[0]} onValueChange={(v) => changeRole(m.id, v)}>
                    <SelectTrigger data-testid={`role-${m.id}`} className="h-8 w-40 border-zinc-800 bg-zinc-950 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900">{ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLES[r]}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {m.status === "invited" && !m.is_self && (
                  <button data-testid={`resend-${m.id}`} onClick={() => resendInvite(m.id)} title="Renvoyer l'invitation" className="p-2 text-zinc-500 hover:text-[#FF6A00]"><Send className="h-4 w-4" /></button>
                )}
                {!m.is_self && <button data-testid={`del-member-${m.id}`} onClick={() => setDelTarget(m)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="unav" className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-zinc-500">Congés et absences de l'équipe.</p>
            <Dialog open={uOpen} onOpenChange={setUOpen}>
              <DialogTrigger asChild><Button data-testid="add-unav-btn" className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Indisponibilité</Button></DialogTrigger>
              <DialogContent className="border-zinc-800 bg-zinc-950">
                <DialogHeader><DialogTitle className="text-white">Nouvelle indisponibilité</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400">Collaborateur</Label>
                    <Select value={uForm.user_id || "me"} onValueChange={(v) => setUForm({ ...uForm, user_id: v === "me" ? "" : v })}>
                      <SelectTrigger data-testid="unav-member" className="mt-1 border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">
                        <SelectItem value="me">Moi ({user?.name})</SelectItem>
                        {members.filter((m) => !m.is_self).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-zinc-400">Du</Label><Input data-testid="unav-start" type="date" className="mt-1 border-zinc-800 bg-zinc-900" value={uForm.start_date} onChange={(e) => setUForm({ ...uForm, start_date: e.target.value })} /></div>
                    <div><Label className="text-zinc-400">Au</Label><Input data-testid="unav-end" type="date" className="mt-1 border-zinc-800 bg-zinc-900" value={uForm.end_date} onChange={(e) => setUForm({ ...uForm, end_date: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-zinc-400">Motif</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Congés, formation…" value={uForm.reason} onChange={(e) => setUForm({ ...uForm, reason: e.target.value })} /></div>
                  <Button data-testid="save-unav" onClick={addUnav} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {unav.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-800 py-12 text-center"><CalendarOff className="mx-auto h-10 w-10 text-zinc-700" /><p className="mt-3 text-zinc-500">Aucune indisponibilité déclarée.</p></div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
              {unav.map((u) => (
                <div key={u.id} className="flex items-center gap-4 border-b border-zinc-800 p-4 last:border-0">
                  <CalendarOff className="h-5 w-5 text-red-400" />
                  <div className="flex-1"><p className="font-medium text-white">{u.user_name}</p><p className="text-sm text-zinc-500">{frDate(u.start_date)} → {frDate(u.end_date)}{u.reason ? ` · ${u.reason}` : ""}</p></div>
                  <button onClick={() => delUnav(u.id)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950 text-white" data-testid="del-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white"><Trash2 className="h-5 w-5 text-red-400" />Supprimer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {delTarget && <>Vous êtes sur le point de supprimer <span className="font-semibold text-white">{delTarget.name}</span> ({delTarget.email}).<br /><br /></>}
              Conformément au <span className="font-medium text-zinc-200">RGPD</span>, toutes les données liées à ce compte seront <span className="font-medium text-zinc-200">définitivement supprimées</span> : accès, notifications, indisponibilités, journal d'activité et affectations aux tâches. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="del-member-cancel" className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-white">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="del-member-confirm" onClick={() => delMember(delTarget.id)} className="bg-red-600 text-white hover:bg-red-700">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
